/** @file Hierarchy tree of a document: bones by parent and sibling order, their slots, default-skin entries. */
import type { Doc, DocIndex, Id } from '@pose2d/format/types';
import { ROW_BADGES as B } from '../../forms/rowBadges.data';
import type { TreeNode } from '../../ui-kit/logic/flattenTree';

const byText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Appends `value` to the list under `key`. */
function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

/** Slot ids per bone, in setup draw order (bottom first). */
function slotsByBone(doc: Doc): Map<Id, Id[]> {
  const out = new Map<Id, Id[]>();
  for (const id of doc.drawOrder) {
    const bone = doc.slots[id]?.bone;
    if (bone !== undefined) push(out, bone, id);
  }
  return out;
}

/** Placeholder rows per slot from the default skin, by name; ● marks the slot's setup attachment. */
function entriesBySlot(doc: Doc): Map<Id, TreeNode[]> {
  const names = new Map<Id, string[]>();
  for (const e of doc.skins[doc.defaultSkin]?.entries ?? []) push(names, e.slot, e.placeholder);
  const out = new Map<Id, TreeNode[]>();
  for (const [slot, list] of names) {
    const shown = doc.slots[slot]?.setup.attachment ?? null;
    out.set(slot, list.sort(byText).map((p) => ({ id: `${slot}:${p}`, label: p, badge: p === shown ? B.shown : B.hidden })));
  }
  return out;
}

/** Root bones (one in a valid document), each with its child bones (sibling order), then its slots (draw order). */
export function buildHierarchy(doc: Doc, index: DocIndex): TreeNode[] {
  const slots = slotsByBone(doc);
  const entries = entriesBySlot(doc);
  const slotNode = (id: Id): TreeNode => ({ id, label: doc.slots[id]?.name ?? id, badge: B.slot, children: entries.get(id) ?? [] });
  const boneNode = (id: Id): TreeNode => {
    const bone = doc.bones[id];
    const children = [...(index.children.get(id) ?? []).map(boneNode), ...(slots.get(id) ?? []).map(slotNode)];
    return { id, label: bone?.name ?? id, badge: bone?.kind === undefined ? B.bone : B.socket, children };
  };
  return Object.entries(doc.bones).filter(([, b]) => b.parent === null).map(([id]) => boneNode(id));
}
